import { anyDayToWeekdayNum } from '@sub/utils/weeks';
import type { Cell } from './Cell'
import { dateToTMinutes } from '@sub/utils/times';
import { isBetween } from '@sub/utils/comparisons';

export interface IPeriodParams {
  days: string[]
  start: number
  end: number
}

function getEndOfPeriod(period: Period, day: Date) {
  const endDate = new Date(day);
  endDate.setHours(0, period.end);
  return endDate;
}

function checkWeekday(period: Period, d: Date) {
  return period.days.includes(anyDayToWeekdayNum(d));
}

export function isPeriodIncludeByDate(period: Period, cellOrDate: Cell | Date) {
  if (cellOrDate instanceof Date) return checkWeekday(period, cellOrDate);
  return checkWeekday(period, cellOrDate.from) || checkWeekday(period, cellOrDate.to)
}

function isPeriodIncludeByTime(period: Period, time: Date) {
  return isBetween(period.start, dateToTMinutes(time), period.end);
}

function isPeriodInclude(period: Period, date: Date) {
  const isTime = isPeriodIncludeByTime(period, date)
  if (!isTime) return false

  return isPeriodIncludeByDate(period, date);
}

export function getPeriod({ days, start, end }: IPeriodParams) {
  return {
    days: days.map(anyDayToWeekdayNum),
    start,
    end,
  }
}

export type Period = ReturnType<typeof getPeriod>;

/**
 * Режим работа состоит из рабочих часов на каждый день недели
 * Рабочие часы состоят из временных групп, которые могут следовать друг за другом (по идее отсортированы, но не гарантированно)
 */
export function usePeriodList(items: Period[], timeOffset = 0) {
  const sortedPeriods = computed(() => items.sort((a, b) => {
    /** Сортировка по дням недели */
    const diffByDays = a.days[0] - b.days[0];
    if (diffByDays !== 0) return diffByDays;

    /** Сортировка по часам */
    return a.start - b.start;
  }));

  function getNextWeekNumber(n: number) {
    return (n + 1) % items.length;
  }

  function getStartWithOffset(start: number) {
    return start <= timeOffset ? (1440 + timeOffset) : start;
  }

  function isPeriodsAreContinuous(periodStart: Period, periodEnd: Period) {
    const startIndex = sortedPeriods.value.findIndex(p => p === periodStart);
    const endIndex = sortedPeriods.value.findIndex(p => p === periodEnd);

    for (let i = startIndex, nextIndex = getNextWeekNumber(i); i + 1 <= endIndex; i = getNextWeekNumber(i), nextIndex = getNextWeekNumber(i)) {
      if (items[i].end !== getStartWithOffset(items[nextIndex].start)) return false;
    }

    return true;
  }

  function checkPeriod(cellOrDate: Cell | Date) {
    if (items && items?.length) {
      const isDate = cellOrDate instanceof Date

      const dateFrom = isDate ? cellOrDate : cellOrDate.from

      /** Рабочие часы для начала периода */
      const periodWithFrom = items.find((period) => isPeriodInclude(period, dateFrom))
      if (!periodWithFrom) return { error: true };

      const _dateTo = isDate ? cellOrDate : cellOrDate.to;
      // TODO hot fix для проверки 00:00 (24:00) на вхождение в период
      const dateTo = _dateTo.getHours() === 0 && _dateTo.getMinutes() === 0 ? subMinutes(_dateTo, 1) : _dateTo;
      /** Проверка на вхождение конца периода в это рабочее время */
      const isPeriodWithFromExcludedTo = !isPeriodInclude(periodWithFrom, dateTo);
      if (isPeriodWithFromExcludedTo) {
        /** Проверка на вхождение конца периода в другие рабочие часы */
        const periodWithTo = items.find((period) => isPeriodInclude(period, dateTo))
        if (periodWithTo) {
          if (isPeriodsAreContinuous(periodWithFrom, periodWithTo)) return { error: false };
          /** Проверка на наличие непрерывного рабочего периода между началом и концом периода */
          return { error: true, periodEnd: getEndOfPeriod(periodWithTo, dateTo) };
        }
        return { error: true, periodEnd: getEndOfPeriod(periodWithFrom, dateFrom) };
      }
    }

    return { error: false };
  }

  return {
    checkPeriod,
  };
}
